/**
 * Platform bootstrap (H §3; contract ported from dink-admin DNKADM-0274).
 * `env.apiBaseUrl` (the same-origin `/api` prefix) is the only base this app may
 * compute; the login and auth hosts are platform singletons it cannot derive.
 * This module asks tmm-api for them once, at app start, and holds the answer
 * **in memory only**: no `localStorage`/`sessionStorage`/cookie/service worker,
 * no TTL, no refresh timer. Persisting is actively harmful — a cached
 * `loginUrl` would outlive an operator changing the product's hostnames.
 *
 * On `reseller.tmm.<base>` tmm-api answers the **product-console** body
 * `{ productSlug, productName, loginUrl, authOrigin, deployEnv? }`. A body that
 * carries `tenantSlug` means the tenant branch answered (a mis-dispatch) and is a
 * failure; so is a `loginUrl`/`authOrigin` that is not `https:` (a `localhost`
 * origin is allowed for local runs and tests).
 *
 * `GET {apiBase}/bootstrap` is **unauthenticated** by design — it tells an
 * unauthenticated visitor where to log in — so it is issued with `fetch`
 * (never the configured axios instance, whose interceptors assume a resolved
 * auth base; using it here to resolve the auth base would be circular) and
 * `credentials: 'omit'` explicitly.
 */
import { env } from './env';

export interface Bootstrap {
  productSlug: string;
  /** Display name of the product; the app-bar fallback when the reseller brand can't load. */
  productName: string | null;
  /** saas-login in reseller mode (`https://<login host>/login`). Canonical, carries NO query
   *  string. Used verbatim. */
  loginUrl: string;
  /** saas-auth for this console (`https://auth.tmm.<base>`) — where the refresh cookie lives.
   *  Used verbatim. */
  authOrigin: string;
  deployEnv: string | null;
}

/**
 * A resolved bootstrap failure. `status` is the HTTP status when the request
 * reached the server, or `null` for a transport-level failure (network error,
 * malformed body). `code` is tmm-api's envelope `error.code` when the failure
 * body was JSON (`HOSTNAME_NOT_REGISTERED`, `CONSOLE_NOT_CONFIGURED`,
 * `UPSTREAM_UNAVAILABLE`), else `null`. The startup gate maps both to copy.
 */
export class BootstrapError extends Error {
  readonly status: number | null;
  readonly code: string | null;

  constructor(message: string, status: number | null, code: string | null = null) {
    super(message);
    this.name = 'BootstrapError';
    this.status = status;
    this.code = code;
  }
}

let resolved: Bootstrap | null = null;

/**
 * Resolve bootstrap from the product API and cache it in memory. Throws
 * `BootstrapError` on any failure — there is never a composed fallback; no
 * certificate exists for one.
 */
export async function resolveBootstrap(): Promise<Bootstrap> {
  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}/bootstrap`, { credentials: 'omit' });
  } catch {
    throw new BootstrapError('Could not reach the platform bootstrap endpoint.', null);
  }
  if (!response.ok) {
    throw new BootstrapError(
      `Bootstrap request failed with status ${response.status}.`,
      response.status,
      await errorCodeOf(response),
    );
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new BootstrapError('Bootstrap response was not valid JSON.', response.status);
  }
  const parsed = parseBootstrap(body);
  if (!parsed) {
    throw new BootstrapError(
      'Bootstrap response is not the product-console shape (missing productSlug/loginUrl/authOrigin, a non-https URL, or a tenant body).',
      response.status,
    );
  }
  resolved = parsed;
  return parsed;
}

/**
 * The resolved bootstrap. Throws if called before `resolveBootstrap()` has
 * succeeded — there is no default, placeholder, or composed fallback. The
 * startup gate guarantees nothing renders (and so nothing calls this) before
 * bootstrap resolves; this throw is the guard if that ever changes.
 */
export function getBootstrap(): Bootstrap {
  if (!resolved) {
    throw new Error('getBootstrap() called before bootstrap resolved.');
  }
  return resolved;
}

/**
 * True when `value` is an absolute `https:` URL — or an `http:` URL on `localhost`
 * in dev and test runs only; a built bundle — staging or production — requires `https:`.
 * Every bootstrapped URL is checked with this at parse time and again immediately before
 * any `location.assign` (H §9.2).
 */
export function isSafeBootstrapUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol === 'https:') return true;
  return url.protocol === 'http:' && url.hostname === 'localhost' && !import.meta.env.PROD;
}

async function errorCodeOf(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { error?: { code?: unknown } } | null;
    const code = body?.error?.code;
    return typeof code === 'string' && code.length > 0 ? code : null;
  } catch {
    return null;
  }
}

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Parse the bootstrap body defensively. Returns `null` (a failure) when a required
 * field is missing or blank, when a URL is not https, or when the body carries
 * `tenantSlug` (the tenant branch answered). Origins are carried verbatim — never
 * parsed into parts, rebuilt, normalized, or stripped/appended.
 */
function parseBootstrap(body: unknown): Bootstrap | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if ('tenantSlug' in b) return null;
  const productSlug = requiredString(b.productSlug);
  const loginUrl = requiredString(b.loginUrl);
  const authOrigin = requiredString(b.authOrigin);
  if (!productSlug || !loginUrl || !authOrigin) return null;
  if (!isSafeBootstrapUrl(loginUrl) || !isSafeBootstrapUrl(authOrigin)) return null;
  return {
    productSlug,
    productName: requiredString(b.productName),
    loginUrl,
    authOrigin,
    deployEnv: requiredString(b.deployEnv),
  };
}

/** Test seam — seed or clear the resolved bootstrap without a real fetch. */
export function __setBootstrapForTest(bootstrap: Bootstrap | null): void {
  resolved = bootstrap;
}
